/**
 * Page order, ids and labels for every section. `App.tsx` renders from this list, and
 * playground mode (`?section=<id>`) looks sections up here.
 *
 * Owned by the Architect: builders change their section's internals, never this list.
 *
 * @module
 */
import type { ComponentType } from 'react'
import { Call } from './Call'
import { Cta } from './Cta'
import { Footer } from './Footer'
import { Hero } from './Hero'
import { IdentityLab } from './IdentityLab'
import { Manifesto } from './Manifesto'
import { Markets } from './Markets'
import { Pipeline } from './Pipeline'
import { Showcase } from './Showcase'
import { Storefront } from './Storefront'

/** DOM id of each section (`<section id>`), also used in `#anchor` links and `?section=`. */
export type SectionId =
  | 'hero'
  | 'manifesto'
  | 'call'
  | 'pipeline'
  | 'storefront'
  | 'identity'
  | 'showcase'
  | 'markets'
  | 'start'
  | 'footer'

/** One entry in {@link SECTIONS}. */
export interface SectionEntry {
  id: SectionId
  /** Human label for the dev index and debugging. */
  label: string
  Component: ComponentType
}

/** Every section, in page order. The footer is last and renders outside `<main>`. */
export const SECTIONS: readonly SectionEntry[] = [
  { id: 'hero', label: 'Hero', Component: Hero },
  { id: 'manifesto', label: 'Manifesto', Component: Manifesto },
  { id: 'call', label: 'Call', Component: Call },
  { id: 'pipeline', label: 'Pipeline · How it works', Component: Pipeline },
  { id: 'storefront', label: 'Storefront · What it does', Component: Storefront },
  { id: 'identity', label: 'Identity lab', Component: IdentityLab },
  { id: 'showcase', label: 'Showcase · Work', Component: Showcase },
  { id: 'markets', label: 'Markets', Component: Markets },
  { id: 'start', label: 'CTA · Start', Component: Cta },
  { id: 'footer', label: 'Footer', Component: Footer },
]

/** Looks a section up by id (for `?section=`). Returns `undefined` for unknown ids. */
export function findSection(id: string): SectionEntry | undefined {
  return SECTIONS.find((section) => section.id === id)
}
